/**
 * Mirrors interlude's `openapi/constants.NAME_MAX_LENGTH`.
 *
 * The name is stored as a Kong `name:<value>` tag, so the value has to satisfy
 * Kong's tag rules. Interlude enforces them in `name_tag_violation()`; sending
 * a value that breaks them comes back as a 422 carrying no error code this app
 * maps, so the form has to reject it first.
 */
export const API_KEY_NAME_MAX_LENGTH = 128;

/**
 * Why the name is validated rather than rewritten: the reader has to be able to
 * find this key again by the name they chose. Silently dropping the space out
 * of "order sync" would leave them looking for a key that no longer matches
 * what they typed, so the field explains the rule instead.
 */
export type ApiKeyNameViolation = "required" | "tooLong" | "forbiddenCharacter";

/**
 * Kong 3.x tag charset: UTF-8 except `,`, `/` and non-printable ASCII, which
 * includes the space. Interlude spells this as a character class covering
 * code points 0x00 to 0x20 plus those two; expressed here as code point
 * comparisons, because a regex literal carrying control characters trips
 * `no-control-regex` and reads worse than the rule it encodes.
 */
function hasForbiddenCharacter(name: string): boolean {
  for (const character of name) {
    const codePoint = character.codePointAt(0);
    if (codePoint !== undefined && codePoint <= 0x20) return true;
    if (character === "," || character === "/") return true;
  }
  return false;
}

/**
 * Validate a name against the backend's rules.
 *
 * @returns the rule that was broken, or `null` when the name is acceptable.
 */
export function validateApiKeyName(name: string): ApiKeyNameViolation | null {
  // Interlude rejects a blank name before the tag rules, and in the same
  // order: a string of spaces is "required", not "forbidden".
  if (name.trim().length === 0) return "required";
  if (name.length > API_KEY_NAME_MAX_LENGTH) return "tooLong";
  if (hasForbiddenCharacter(name)) return "forbiddenCharacter";
  return null;
}
